import React, { ReactNode, useMemo } from "react"
import useClx from "../../../hooks/use-clx";
import clxs from "./cluster-summary.module.css";
import format from "date-fns/format";
import { localEquivalent } from "../../../../utils/date-tz";

import MEAL_ICON from "../../../assets/icons/ramen-dining.svg";
import CHECK_IN_OUT_ICON from "../../../assets/icons/checkin_out_add.svg";
import CALENDAR_CLOCK from "../../../assets/icons/calendar_clock.svg";
import CHECK_IN_OUT_ARROW_ICON from "../../../assets/icons/arrow_right_alt.svg";
import BEDROOM_ICON from "../../../assets/icons/bed-icon.svg";

interface ClusterSummaryProps {
  viewLayout: string;
  booking_item: ClusterBookingItem[];
  className?: string;
  checkinDate: Date | null;
  checkoutDate: Date | null;
  adultCount: number;
  childCount: number;
  preBookMeals: boolean;
  configArgs: PropertyConfigArg[];
  configMap: Record<number, PropertyConfig>;
  onDateChange?: () => void;
  checkOutTime?:string;
  checkInTime?:string;
}

function ClusterSummary(props: ClusterSummaryProps) {
  const { className, configArgs, configMap, viewLayout } = props,
    ccx = useClx(clxs.container, className),
    mtcx = useClx(clxs.title, clxs.mobile),
    dscx = useClx(clxs.subtitle, clxs.desktop),
    {
      booking_item,
      checkinDate,
      checkoutDate,
      adultCount,
      preBookMeals,
      childCount,
      checkOutTime,
      checkInTime,
      onDateChange,
    } = props,
    { checkin, checkinTime, checkout, checkoutTime } = useMemo(
      () => {
        const checkin = checkinDate
          ? format(localEquivalent(checkinDate), "ccc, dd LLL")
          : "",
          checkinTime = checkInTime
            ? checkInTime
            : "2:00 pm",
          checkout = checkoutDate
            ? format(localEquivalent(checkoutDate), "ccc, dd LLL")
            : "",
          checkoutTime = checkOutTime
            ? checkOutTime
            : "11:00 am";

        return {
          checkin: checkin,
          checkinTime: checkinTime,
          checkout: checkout,
          checkoutTime: checkoutTime,
        };
      },
      [checkinDate, checkoutDate,checkInTime,checkOutTime],
    ),
    sumOfRooms = useMemo(
      () => configArgs.reduce(
        (compiled, each) => {
          return each.required_rooms + compiled;
        },
        0,
      ),
      [configArgs],
    );

  return (
    <div className={ccx}>
      <div className={clxs.header}>
        <div className="">Booking Summary</div>
        <div
          onClick={onDateChange}
          className={clxs.changeDate}
        >
          Edit Booking
        </div>
      </div>
      <div className={clxs.summaryContainer}>
        {booking_item.map((config, index) => (
          <React.Fragment key={index}>
            <div
              className={clxs.villaSection}
            >

              <div className={clxs.thumbnailContainer}>
                <img
                  src={config.images[0].url}
                  alt={"thumbnail"}
                  className={clxs.thumbnail}
                />
              </div>
              <div
                className={clxs.titlePartial}
              >
                <div className={clxs.propertyName}>{config.villa_name}</div>
                <div
                  title={config.location}
                  className={clxs.propertyAddress}
                >
                  {config.location}
                </div>

                <div className={clxs.specContainer}>

                  <div className={clxs.spec}>
                    {config.max_occupancy} guests
                  </div>

                  {config.bedrooms > 0 ? <span className={clxs.dot}>•</span> : null}

                  <div className={clxs.spec}>
                    {config.bedrooms} {config.bedrooms > 1 ? "Beds" : "Bed"}
                  </div>

                  {config.bathrooms > 0 ? <span className={clxs.dot}>•</span> : null}

                  <div className={clxs.spec}>
                    {config.bathrooms} {config.bathrooms > 1 ? "Bathrooms" : "Bathroom"}
                  </div>

                  {config.pools > 0 ? <span className={clxs.dot}>•</span> : null}

                  <div className={clxs.spec}>
                    {config.pools} {config.pools > 1 ? "Pools" : "Pool"}
                  </div>

                </div>
              </div>

            </div>

            {index < booking_item.length - 1 && <div className={clxs.divider} />}
          </React.Fragment>
        ))}

      </div>
      <div className={clxs.datePartial}>
        <div className={dscx}>
          <div className={clxs.occupancyIcon}>
            <img
              src={CALENDAR_CLOCK}
              alt={"CALENDAR_CLOCK"}
              className={clxs.icon}
            />
          </div>

          <div className={clxs.occupancyTitle}>
            Check in
          </div>
          <div className={clxs.occupancy}>
            {checkin} <span className={clxs.timeText}>({checkinTime})</span>
          </div>
        </div>
        <div className={clxs.checkOutInIcon}>
          <img
            src={CHECK_IN_OUT_ARROW_ICON}
            alt={"CHECK_IN_OUT_ARROW_ICON"}
            className={clxs.arrowIcon}
          />
        </div>
        <div className={dscx}>
          <div className={clxs.occupancyIcon}>
            <img
              src={CALENDAR_CLOCK}
              alt={"CALENDAR_CLOCK"}
              className={clxs.icon}
            />
          </div>

          <div className={clxs.occupancyTitle}>
            Check out
          </div>
          <div className={clxs.occupancy}>
            {checkout} <span className={clxs.timeText}>({checkoutTime})</span>
          </div>
        </div>

        <div className={mtcx}>

          <div className={clxs.occupancyIcon}>
            <img
              src={CALENDAR_CLOCK}
              alt={"CALENDAR_CLOCK"}
              className={clxs.icon}
            />
          </div>

          <div className={clxs.occupancyTitle}>
            Check in - Check out
          </div>
          <div className={clxs.occupancy}>
            {checkin} <span className={clxs.timeText}>({checkinTime})</span> - {checkout} <span className={clxs.timeText}>({checkoutTime})</span>
          </div>
        </div>
        <div className={clxs.occupancyContainer}>
          <div className={clxs.occupancyIcon}>
            <img
              src={CHECK_IN_OUT_ICON}
              alt={"CHECK_IN_OUT_ICON"}
              className={clxs.icon}
            />
          </div>

          <div className={clxs.occupancyTitle}>
            Total guest count
          </div>
          <div className={clxs.occupancy}>
            {adultCount} Adults, {childCount} Children
          </div>
        </div>
      </div>
      {viewLayout === "multi_config" && (
        <div className={clxs.roomSummary}>
          <img
            src={BEDROOM_ICON}
            alt={"BEDROOM_ICON"}
            className={clxs.bedIcon}
          />
          <div className={clxs.roomDetails}>
            <div className={clxs.roomCount}>
              {`${sumOfRooms} rooms`}
            </div>
            <div className={clxs.roomTypes}>
              {configArgs.reduce(
                (compiled, each, key) => {
                  const {
                    property_config_id,
                    required_rooms,
                  } = each,
                    { room_name = "" } = configMap[property_config_id as any] ?? {};
                  if (!required_rooms) {
                    return compiled;
                  }

                  return [
                    ...compiled,
                    <div
                      className={clxs.configRoom}
                      key={key}
                    >
                      {`${room_name} x ${required_rooms}`}
                    </div>,
                  ];
                },
                [] as ReactNode[],
              )}
            </div>
          </div>
          {/* <div
            onClick={onDateChange}
            className={clxs.changeSelection}
          >
            Change Room Selection
          </div> */}
        </div>
      )}
      {preBookMeals == true ?
        <div className={clxs.mealContainer}>
          <img
            src={MEAL_ICON}
            className={clxs.mealIcon}
            alt="Meal Options"
          />
          <span className={clxs.mealPlan}>
            I’m interested to pre-book meals
          </span>
        </div> : null}
    </div>
  );
}

export default ClusterSummary
